import { Injectable, isDevMode } from '@angular/core';
import { CatalogDaoService } from 'src/app/dao/catalogs-dao-services/catalogs-dao-services/catalog-dao.service';
import { map, tap } from 'rxjs/operators';
import { UserStorageService } from '../../user-storage-service/user-storage.service';
import { CatalogBasic } from 'src/app/models/catalogs-model/catalog.model';
import { Subject } from 'rxjs';
import { DeviceBasic } from 'src/app/models/device-models/device-basic-model/device-basic.model';
import { DeskBasic } from 'src/app/models/turns-models/desk-basic.model';
import { CenterLite } from 'src/app/models/center-model/center-lite.model';
import { ProductBasic } from 'src/app/models/catalogs-model/product.model';
import { CatalogDesk } from 'src/app/models/catalogs-model/catalog-desk.model';

@Injectable({
  providedIn: 'root'
})
export class CatalogsService {

  refreshCatalogsListener = new Subject<any>();
  refreshCatalogListener = new Subject<any>();
  refreshCatalogDesksListener = new Subject<any>();
  refreshCatalogDevicesMCatalogListener = new Subject<any>();
  refreshCatalogDevicesStaffListener = new Subject<any>();
  filterCatalogsListener = new Subject<any>();
  searchCatalogsListener = new Subject<any>();
  toggleCatalogsDispositionListener = new Subject<any>();

  constructor(private userStorageService: UserStorageService,
              private catalogsDaoService: CatalogDaoService) { }

  public getCatalogsCounts() {
    return this.catalogsDaoService.getCatalogsCounts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => {
        if (o) {
          return {
            availableCatalogs: o['data']['catalogs']['nbAvailables'],
            unavailableCatalogs: o['data']['catalogs']['nbDisabled']
          };
        }
      })
    );
  }

  public getCatalogsList() {
    return this.catalogsDaoService.getCatalogsList(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => {
        if (o) {
          const catalogs = new Array<CatalogBasic>();
          o['data']['catalogs'].forEach(
            (catalog) => {
              catalogs.push(Object.assign(new CatalogBasic(), catalog));
            }
          );
          return catalogs;
        }
      })
    );
  }

  public getCatalogInfo(internalId) {
    return this.catalogsDaoService.getCatalogInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => {
        const catalog = new CatalogBasic();
        Object.assign(catalog, o['data']['catalog']);
        const centers = new Array<CenterLite>();
        o['data']['catalog']['centers'].forEach(c => centers.push(Object.assign(new CenterLite(), c)));
        catalog.Centers = centers;
        const desks = new Array<CatalogDesk>();
        o['data']['catalog']['turnDesks'].forEach(d => desks.push(Object.assign(new CatalogDesk(), d)));
        catalog.Desks = desks;
        const devicesMCatalog = new Array<DeviceBasic>();
        o['data']['catalog']['devicesMCatalog'].forEach(d => devicesMCatalog.push(Object.assign(new DeviceBasic(), d)));
        catalog.DevicesMCatalog = devicesMCatalog;
        const devicesStaff = new Array<DeviceBasic>();
        o['data']['catalog']['devicesStaff'].forEach(d => devicesStaff.push(Object.assign(new DeviceBasic(), d)));
        catalog.DevicesStaff = devicesStaff;
        const products = new Array<ProductBasic>();
        o['data']['catalog']['products'].forEach(p => products.push(Object.assign(new ProductBasic(), p)));
        catalog.Products = products;
        return catalog;
      })
    );
  }

  public getCatalogDevicesMCatalog(centerInternalIds) {
    return this.catalogsDaoService.getCatalogDevicesMCatalog(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalIds).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => {
        if (o) {
          const devices = new Array<DeviceBasic>();
          o['data']['devices'].forEach(d => {
            devices.push(Object.assign(new DeviceBasic(), d));
          });
          return devices;
        }
      })
    );
  }

  public getCatalogDevicesStaff(centerInternalIds) {
    return this.catalogsDaoService.getCatalogDevicesStaff(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalIds).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => {
        if (o) {
          const devices = new Array<DeviceBasic>();
          o['data']['devices'].forEach(d => {
            devices.push(Object.assign(new DeviceBasic(), d));
          });
          return devices;
        }
      })
    );
  }

  public getCatalogDesks(centerInternalIds) {
    return this.catalogsDaoService.getCatalogDesks(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalIds).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => {
        if (o) {
          const desks = new Array<DeskBasic>();
          o['data']['desks'].forEach(d => {
            desks.push(Object.assign(new DeskBasic(), d));
          });
          return desks;
        }
      })
    );
  }

  public addNewCatalog(catalog) {
    return this.catalogsDaoService.addNewCatalog(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), catalog.get('image').value, catalog.get('active').value, catalog.get('name').value, catalog.get('position').value, catalog.get('backgroundColor').value, catalog.get('textColor').value, catalog.get('printInmediately').value).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data'])
    );
  }

  public updateCatalogImage(internalId, imageUrl) {
    return this.catalogsDaoService.updateCatalogImage(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, imageUrl).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o)
    );
  }

  public updateCatalogStatus(internalId, active) {
    return this.catalogsDaoService.updateCatalogStatus(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data'])
    );
  }

  public updateCatalogInfo(internalId, catalog) {
    return this.catalogsDaoService.updateCatalogInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, catalog.get('active').value, catalog.get('name').value, catalog.get('position').value, catalog.get('backgroundColor').value, catalog.get('textColor').value).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data'])
    );
  }

  public updateCatalogCenters(internalId, centersInternalIds) {
    return this.catalogsDaoService.updateCatalogCenters(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, centersInternalIds).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data'])
    );
  }

  public updateCatalogDesks(internalId, desksInternalIds) {
    return this.catalogsDaoService.updateCatalogDesks(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, desksInternalIds).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data'])
    );
  }

  public updateCatalogDevicesMCatalog(internalId, devicesInternalIds) {
    return this.catalogsDaoService.updateCatalogDevicesMCatalog(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, devicesInternalIds).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data'])
    );
  }

  public updateCatalogDevicesStaff(internalId, devicesInternalIds) {
    return this.catalogsDaoService.updateCatalogDevicesStaff(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, devicesInternalIds).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data'])
    );
  }

  public updateCatalogPrintInmediately(internalId, updateCatalogPrintInmediately) {
    return this.catalogsDaoService.updateCatalogPrintInmediately(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, updateCatalogPrintInmediately).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data'])
    );
  }

  public updateCatalogProducts(internalId, products) {
    return this.catalogsDaoService.updateCatalogProducts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, products).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data'])
    );
  }

  public deleteCatalog(id) {
    return this.catalogsDaoService.deleteCatalog(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), id).pipe(
      tap(o => !isDevMode() || console.dir(o)),
      map(o => o['data'])
    );
  }

  public refreshCatalogs() {
    this.refreshCatalogsListener.next();
  }

  public refreshCatalogsListen() {
    return this.refreshCatalogsListener.asObservable();
  }

  public refreshCatalog() {
    this.refreshCatalogListener.next();
  }

  public refreshCatalogListen() {
    return this.refreshCatalogListener.asObservable();
  }

  public refreshCatalogDesks() {
    this.refreshCatalogDesksListener.next();
  }

  public refreshCatalogDesksListen() {
    return this.refreshCatalogDesksListener.asObservable();
  }

  public refreshCatalogDevicesMCatalog() {
    this.refreshCatalogDevicesMCatalogListener.next();
  }

  public refreshCatalogDevicesMCatalogListen() {
    return this.refreshCatalogDevicesMCatalogListener.asObservable();
  }
  public refreshCatalogDevicesStaff() {
    this.refreshCatalogDevicesStaffListener.next();
  }

  public refreshCatalogDevicesStaffListen() {
    return this.refreshCatalogDevicesStaffListener.asObservable();
  }

  public filterCatalogs(opts) {
    this.filterCatalogsListener.next(opts);
  }

  public filterCatalogsListen() {
    return this.filterCatalogsListener.asObservable();
  }

  public searchCatalogs(key) {
    this.searchCatalogsListener.next(key);
  }

  public searchCatalogsListen() {
    return this.searchCatalogsListener.asObservable();
  }

  public toggleCatalogsDisposition(viewDis) {
    this.toggleCatalogsDispositionListener.next(viewDis);
  }

  public toggleCatalogsDispositionListen() {
    return this.toggleCatalogsDispositionListener.asObservable();
  }
}
